import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { RecruitingDealsService } from 'src/app/recruiting/deals/deals.service';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import {
    Attachment,
    DetailLookups,
    Expertise,
    Facilities,
    FacilityLookups,
    GridData,
    GridLookups,
    Offer,
    OfferDetails,
    OfferJobDetails,
    OfferNotes,
    OfferRates,
    OfferType,
    VendorLookups
} from 'src/app/shared/models/offers.model';
import { OfferMarginDifference } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/model/offer-margin-difference.model';
import { appendQuery, combine, getQueryString } from 'src/app/shared/utilities/route';
import { OfferStatus } from '../models/enums/offers.enum';
import { ListItem } from '../models/list-item.model';
import { SubmittalSendToClientUpdateResult } from 'src/app/accountmanagers/submittals/am-submittal-status-update/model/am-submittal-send-to-client-update-result.model';
import { CandidateOfferNoteCategory } from 'src/app/shared/models/enums/candidate-offer-note-category.enum';
dayjs.extend(utc);

export interface FileValidationResult {
    name: string;
    isValid?: boolean;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SharedOffersService {
    private readonly coreApiPath: string;
    private readonly v1Domain: string;
    private readonly accountManagerPath: string;
    private readonly partnerNetworkPath: string;
    private readonly attachmentsPath: string;
    private readonly _dateParamFormat: string = 'YYYY-MM-DD';
    accountManagerArgs: any;
    accountManagerFilters: any;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        private readonly http: HttpClient,
        private readonly recruitingDealsService: RecruitingDealsService
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/');
        this.v1Domain = settings.v1Domain;
        this.accountManagerPath = combine(this.coreApiPath, 'candidate-offer/account-manager');
        this.partnerNetworkPath = combine(this.coreApiPath, 'candidate-offer/partner-network');
        this.attachmentsPath = combine(this.coreApiPath, 'candidate-offer/attachments');
    }

    protected getPaged<T>(pageSize: number, skip: number, url: string, params: any): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<T[]>>(path, { params });
    }

    /** Account Managers **/
    getOffersGridDataAM(args: FetchArgs): Observable<PagingToken<GridData[]>> {
        const url = this.accountManagerPath;
        const params = this.recruitingDealsService.cleanupArgs(args);

        this.accountManagerArgs = args;
        this.accountManagerFilters = params;

        return this.getPaged(args.pageSize, args.skip, url, params);
    }

    getOfferAM(id: number): Observable<Offer> {
        const url = `${this.accountManagerPath}/${id}`;
        return this.http.get<Offer>(url);
    }

    getOfferDetailsAM(id: number): Observable<OfferDetails> {
        const url = `${this.accountManagerPath}/${id}/edit`;
        return this.http.get<OfferDetails>(url);
    }

    getOfferDetailsLookupsAM(id: number, isNewOffer = false): Observable<DetailLookups> {
        const url = `${this.accountManagerPath}/lookups/${id}?isNewOffer=${isNewOffer}`;
        return this.http.get<DetailLookups>(url);
    }

    getNewOfferDetailsLookupsAM(newCandidateOffer = true, id = 0): Observable<DetailLookups> {
        const url = `${this.accountManagerPath}/lookups/${id}?newCandidateOffer=${newCandidateOffer}`;
        return this.http.get<DetailLookups>(url);
    }

    getVendorLookupsAM(vendorId: number, includeAyaVendorContact = false): Observable<VendorLookups> {
        const url = `${this.accountManagerPath}/vendor-lookups/${vendorId}?includeAyaVendorContact=${includeAyaVendorContact}`;
        return this.http.get<VendorLookups>(url);
    }

    getFacilityLookupsAM(id: number, newCandidateOffer = false): Observable<FacilityLookups> {
        const url = `${this.accountManagerPath}/facility-lookups/${id}?newCandidateOffer=${newCandidateOffer}`;
        return this.http.get<FacilityLookups>(url);
    }

    getSystemFacilitiesAM(vendorId: number, systemId: number): Observable<Facilities[]> {
        const url = `${this.accountManagerPath}/system-facilities?vendorId=${vendorId}&systemId=${systemId}`;
        return this.http.get<Facilities[]>(url);
    }

    getOfferSubmittalAM(submittalId: number, offerType: OfferType): Observable<OfferDetails> {
        const url = `${this.accountManagerPath}/submittal/${submittalId}?offerType=${offerType}`;
        return this.http.get<OfferDetails>(url);
    }

    getApnOfferVendorContactId(contractId: number, vendorSubmittalId: number): Observable<number> {
        const url = `${this.coreApiPath}margin-calculator/ApnOfferVendorContactId/${contractId}/${vendorSubmittalId}`;
        return this.http.get<number>(url);
    }

    getExpertiseAM(professionId: number, newCandidateOffer = false): Observable<Expertise[]> {
        const url = `${this.accountManagerPath}/expertises/${professionId}?newCandidateOffer=${newCandidateOffer}`;
        return this.http.get<Expertise[]>(url);
    }

    getJobDetailsAM(jobId: number): Observable<OfferJobDetails> {
        const url = `${this.accountManagerPath}/job/${jobId}`;
        return this.http.get<OfferJobDetails>(url);
    }

    lookupFacilityTypeAM(): Observable<GridLookups> {
        const url = `${this.accountManagerPath}/lookups`;
        return this.http.get<GridLookups>(url);
    }

    putUpdateOfferAM(data: OfferDetails): Observable<any> {
        const url = this.accountManagerPath;
        return this.http.put(url, data, { observe: 'response' });
    }

    putToAPNQueueAM(id: number): Observable<any> {
        const url = `${this.accountManagerPath}/apn-queue/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    putToAPNReviewAM(id: number): Observable<any> {
        const url = `${this.accountManagerPath}/apn-review/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    putToAPNVendorReviewAM(id: number): Observable<any> {
        const url = `${this.accountManagerPath}/vendor-review/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    putToRecruiterReview(id: number): Observable<any> {
        const url = `${this.accountManagerPath}/${id}/recruiter-review`;
        return this.http.put(url, { observe: 'response' });
    }

    putToVerballyAcceptedStatus(offerId: number, isRecruiting: boolean): Observable<SubmittalSendToClientUpdateResult> {
        const recruitingPath = `candidate-offer/recruiting/${offerId}/verbally-accepted`;
        const url = isRecruiting
            ? `${combine(this.coreApiPath, recruitingPath)}`
            : `${this.accountManagerPath}/${offerId}/verbally-accepted`;
        return this.http.put<SubmittalSendToClientUpdateResult>(url, { observe: 'response' });
    }

    getAyaPaidBonusAmount(offerId: number): Observable<number> {
        const url = `${this.coreApiPath}candidate-offer/recruiting/${offerId}/aya-paid-bonus-amount`;
        return this.http.get<number>(url);
    }

    getNotesAM(id: number): Observable<OfferNotes[]> {
        const url = `${this.accountManagerPath}/${id}/notes`;
        return this.http.get<OfferNotes[]>(url);
    }

    postNoteAM(
        id: number,
        note: any,
        offerNoteCategoryId: CandidateOfferNoteCategory = CandidateOfferNoteCategory.ClientFacing
    ): Observable<any> {
        const url = `${this.accountManagerPath}/${id}/notes`;
        return this.http.post<any>(url, { note, offerNoteCategoryId });
    }

    postCreateOfferAM(data: OfferDetails): Observable<number> {
        const url = `${this.accountManagerPath}`;
        return this.http.post<number>(url, data);
    }

    getRatesAM(ratesRequest: any): Observable<OfferRates> {
        const url = `${this.accountManagerPath}/rates`;
        return this.http.get<OfferRates>(url, { params: ratesRequest });
    }

    getVendorRateAM(facilityId: number, facilityRate: number): Observable<number> {
        const url = `${this.accountManagerPath}/vendor-rate?facilityId=${facilityId}&facilityRate=${facilityRate}`;
        return this.http.get<number>(url);
    }

    putToContractRequestedOfferAM(id: number, note: string): Observable<any> {
        const url = `${this.accountManagerPath}/contract-requested/${id}`;
        return this.http.put(url, { note }, { observe: 'response' });
    }

    /** Partner Networks **/
    getOffersGridDataPN(args: FetchArgs): Observable<PagingToken<GridData[]>> {
        const url = this.partnerNetworkPath;
        const params = this.recruitingDealsService.cleanupArgs(args);

        return this.getPaged(args.pageSize, args.skip, url, params);
    }

    getShiftTimeOffers(value, hours) {
        const timeStart = dayjs.utc(value.toString());
        const shiftLength = hours;
        const wholeHour = Math.floor(shiftLength);
        const wholeMin = shiftLength - wholeHour;
        return dayjs(timeStart)
            .add(wholeHour, 'hours')
            .add(wholeMin === 0.5 ? 30 : 0, 'minutes')
            .toDate();
    }

    setTimeEnd(value, form) {
        const shiftToTime = this.getShiftTimeOffers(value, form.get('hours').value);
        const toHour = dayjs(shiftToTime).format('HH');
        const toMin = dayjs(shiftToTime).format('mm');
        const timeEnd = dayjs(shiftToTime).toDate();
        timeEnd.setHours(Number(toHour));
        timeEnd.setMinutes(Number(toMin));
        form.patchValue({ shiftTimeEnd: timeEnd });
        return form.get('shiftTimeEnd').value;
    }

    openV1Facility(facilityId: number): void {
        window.open(
            `//${this.v1Domain}/employers/employersHospitalView.asp?hospID=${facilityId}&navItem=employers`,
            '_blank'
        );
    }

    getOfferPN(id: number): Observable<Offer> {
        const url = `${this.partnerNetworkPath}/${id}`;
        return this.http.get<Offer>(url);
    }

    getOfferDetailsPN(id: number): Observable<OfferDetails> {
        const url = `${this.partnerNetworkPath}/${id}/edit`;
        return this.http.get<OfferDetails>(url);
    }

    getNewOfferDetailsLookupsPN(newCandidateOffer = true, id = 0): Observable<DetailLookups> {
        const url = `${this.partnerNetworkPath}/lookups/${id}?newCandidateOffer=${newCandidateOffer}`;
        return this.http.get<DetailLookups>(url);
    }

    getVendorLookupsPN(vendorId: number): Observable<VendorLookups> {
        const url = `${this.partnerNetworkPath}/vendor-lookups/${vendorId}`;
        return this.http.get<VendorLookups>(url);
    }

    getOfferDetailsLookupsPN(id: number, isNewOffer = false): Observable<DetailLookups> {
        const url = `${this.partnerNetworkPath}/lookups/${id}?isNewOffer=${isNewOffer}`;
        return this.http.get<DetailLookups>(url);
    }

    getOfferSubmittalPN(submittalId: number): Observable<OfferDetails> {
        const url = `${this.partnerNetworkPath}/submittal/${submittalId}`;
        return this.http.get<OfferDetails>(url);
    }

    getFacilityLookupsPN(id: number, newCandidateOffer = false): Observable<FacilityLookups> {
        const url = `${this.partnerNetworkPath}/facility-lookups/${id}?newCandidateOffer=${newCandidateOffer}`;
        return this.http.get<FacilityLookups>(url);
    }

    getSystemFacilitiesPN(vendorId: number, systemId: number): Observable<Facilities[]> {
        const url = `${this.partnerNetworkPath}/system-facilities?vendorId=${vendorId}&systemId=${systemId}`;
        return this.http.get<Facilities[]>(url);
    }

    getExpertisePN(professionId: number): Observable<Expertise[]> {
        const url = `${this.partnerNetworkPath}/expertises/${professionId}`;
        return this.http.get<Expertise[]>(url);
    }

    getJobDetailsPN(jobId: number): Observable<OfferJobDetails> {
        const url = `${this.partnerNetworkPath}/job/${jobId}`;
        return this.http.get<OfferJobDetails>(url);
    }

    lookupFacilityTypePN(): Observable<GridLookups> {
        const url = `${this.partnerNetworkPath}/lookups`;
        return this.http.get<GridLookups>(url);
    }

    putUpdateOfferPN(data: OfferDetails): Observable<any> {
        const url = this.partnerNetworkPath;
        return this.http.put(url, data, { observe: 'response' });
    }

    putToAPNQueuePN(id: number): Observable<any> {
        const url = `${this.partnerNetworkPath}/apn-queue/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    putToAPNReviewPN(id: number): Observable<any> {
        const url = `${this.partnerNetworkPath}/apn-review/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    putToAPNVendorReviewPN(id: number): Observable<any> {
        const url = `${this.partnerNetworkPath}/vendor-review/${id}`;
        return this.http.put(url, { observe: 'response' });
    }

    getNotesPN(id: number): Observable<OfferNotes[]> {
        const url = `${this.partnerNetworkPath}/${id}/notes`;
        return this.http.get<OfferNotes[]>(url);
    }

    postNotePN(id: number, note: any): Observable<any> {
        const url = `${this.partnerNetworkPath}/${id}/notes`;
        return this.http.post<any>(url, { note });
    }

    postCreateOfferPN(data: OfferDetails): Observable<number> {
        const url = `${this.partnerNetworkPath}`;
        return this.http.post<number>(url, data);
    }

    getRatesPN(ratesRequest: any): Observable<OfferRates> {
        ratesRequest.startDate = dayjs(ratesRequest.startDate).format(this._dateParamFormat);
        ratesRequest.endDate = dayjs(ratesRequest.endDate).format(this._dateParamFormat);
        const url = `${this.partnerNetworkPath}/rates`;
        return this.http.get<OfferRates>(url, { params: ratesRequest });
    }

    getVendorRatePN(facilityId: number, facilityRate: number): Observable<number> {
        const url = `${this.partnerNetworkPath}/vendor-rate?facilityId=${facilityId}&facilityRate=${facilityRate}`;
        return this.http.get<number>(url);
    }

    getStatusReasons(statusId: OfferStatus): Observable<ListItem[]> {
        const url = `${this.accountManagerPath}`;
        return this.http.get<ListItem[]>(`${url}/status-reasons/${statusId}`);
    }

    rescindOffer(id: number, reason: any): Observable<any> {
        const url = `${this.accountManagerPath}`;
        return this.http.put<any>(`${url}/rescinded/${id}`, reason);
    }

    /** Attachments */
    getAttachments(offerId: number): Observable<Attachment[]> {
        const url = `${this.attachmentsPath}/${offerId}`;
        return this.http.get<any>(url);
    }

    getAttachmentBlob(offerId: number, id: number): Observable<Blob> {
        const url = `${this.attachmentsPath}/${offerId}/${id}`;
        return this.http.get<any>(url, { responseType: 'blob' as 'json' });
    }

    postAttachment(offerId: number, fileToUpload: any): Observable<Attachment> {
        const input = new FormData();
        input.append('file', fileToUpload);
        const url = `${this.attachmentsPath}/${offerId}`;
        return this.http.post<Attachment>(url, input);
    }

    updateAttachment(offerId: number, attachmentId: number, fileToUpload: any): Observable<FileValidationResult[]> {
        const input = new FormData();
        input.append('file', fileToUpload);
        const url = `${this.attachmentsPath}/${offerId}/${attachmentId}`;
        return this.http.put<FileValidationResult[]>(url, input);
    }

    deleteAttachment(offerId: number, id: number): Observable<any> {
        const url = `${this.attachmentsPath}/${offerId}/${id}`;
        return this.http.delete<any>(url);
    }

    exportOffer(args: FetchArgs): Observable<ArrayBuffer> {
        let url = `${this.accountManagerPath}/export`;
        if (this.accountManagerFilters) {
            url += getQueryString(this.accountManagerFilters);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }
        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this.http.get(path, { responseType: 'arraybuffer' });
    }

    filterJobIds(id: number, facilityLookups: FacilityLookups, candidateProfessionId: number): number[] {
        if (!facilityLookups?.jobs?.length) {
            return [];
        }

        const jobIdSearchTerm = id ? id.toString() : '';

        return facilityLookups.jobs
            .filter(
                (job) =>
                    (!jobIdSearchTerm || job.jobId.toString().startsWith(jobIdSearchTerm)) &&
                    job.professionId === candidateProfessionId
            )
            .map((x) => x.jobId);
    }

    filterJobIdsForCreate(id: number, facilityLookups: FacilityLookups, candidateProfessionId: number): number[] {
        if (!facilityLookups?.jobs?.length) {
            return [];
        }

        const jobIdSearchTerm = id ? id.toString() : '';

        return facilityLookups.jobs
            .filter(
                (job) =>
                    !jobIdSearchTerm ||
                    job.jobId.toString().startsWith(jobIdSearchTerm) ||
                    job.professionId === candidateProfessionId
            )
            .map((x) => x.jobId);
    }

    getOfferMarginDifferences(offerId: number): Observable<OfferMarginDifference[]> {
        const url = `${this.accountManagerPath}/${offerId}/margin-differences`;
        return this.http.get<OfferMarginDifference[]>(url);
    }
}
